import { useEffect, useState } from "react";
import services from "../../services/services";
import AdminPopup from "../places/modal/AdminPopup";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store/app/store";
import { getAdminsThank } from "../../store/features/getAdminsSlice";
import { toast } from "react-toastify";
import AdminDeleteUser from "../adminDeleteUser/AdminDeleteUser";

const defaultPermissions = {
  dashboard: false,
  editUser: false,
  places: false,
  tours: false,
  bundels: false,
  mailTemplate: false,
  appPurchases: false,
  currencyRates: false,
  bookings: false,
  admin: false,
  pages: false,
  settings: false,
};

const permissionLabels: { key: string; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "editUser", label: "Users" },
  { key: "places", label: "Places" },
  { key: "tours", label: "Tours" },
  { key: "bundels", label: "Bundles" },
  { key: "mailTemplate", label: "Mail Template" },
  { key: "appPurchases", label: "App Purchases" },
  { key: "currencyRates", label: "Currency Rates" },
  { key: "bookings", label: "Bookings" },
  { key: "admin", label: "Admin" },
  { key: "pages", label: "Pages" },
  { key: "settings", label: "Settings" },
];

const FormComponent = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adminType, setAdminType] = useState<1 | 2>(2);
  const [permissions, setPermissions] = useState({ ...defaultPermissions });
  const admins = useSelector((state: any) => state.getAdmins);
  const dispatch: any = useAppDispatch();
  const counter = useSelector((state: any) => state.counter);
  const currentUserId = counter?.adminPermission?.id;

  useEffect(() => {
    dispatch(getAdminsThank());
  }, []);

  const adminsList: any[] = admins.Admins?.data?.data || [];
  const superAdmins = adminsList.filter((a: any) => a.is_admin === 1);
  const subAdmins = adminsList.filter((a: any) => a.is_admin === 2);

  const selectedCount = Object.values(permissions).filter(Boolean).length;

  const togglePermission = (key: string) => {
    setPermissions((prev: any) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAllPermissions = () => {
    const allSelected = Object.values(permissions).every(Boolean);
    const newVal = !allSelected;
    const updated: any = {};
    Object.keys(permissions).forEach((k) => (updated[k] = newVal));
    setPermissions(updated);
  };

  const resetModal = () => {
    setShowAddModal(false);
    formik.resetForm();
    setAdminType(2);
    setPermissions({ ...defaultPermissions });
  };

  const handleAddAdmin = async (values: { email: string; password: string; confirmPassword: string }) => {
    // For sub-admin, require at least one permission
    if (adminType === 2 && selectedCount === 0) {
      toast.dismiss();
      toast.error("Please select at least one permission for the sub-admin");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = { email: values.email, adminType, password: values.password };
      if (adminType === 2) {
        // Convert booleans to 0/1 for backend
        payload.permissions = {};
        Object.keys(permissions).forEach((k) => {
          payload.permissions[k] = (permissions as any)[k] ? 1 : 0;
        });
      }
      const res = await services.inviteAdmin(
        payload,
        counter?.adminDetails?.uid
      );
      if (res?.status) {
        toast.dismiss();
        toast.success(res?.data?.message || "Admin added successfully");
        dispatch(getAdminsThank());
        resetModal();
      }
    } catch (err: any) {
      toast.dismiss();
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
    setIsSubmitting(false);
  };

  const formik = useFormik({
    initialValues: { email: "", password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      if (!isSubmitting) handleAddAdmin(values);
    },
  });

  const getActivePermissions = (adminDetails: any) => {
    if (!adminDetails) return [];
    return permissionLabels.filter((p) => adminDetails[p.key]);
  };

  return (
    <div className="px-5">
      {/* Header with Add button */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold font-sans text-gray-800">
            Team Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage super admins and sub-admins with granular access control
          </p>
        </div>
        <button
          className="bg-aqua hover:bg-aquadark text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors"
          onClick={() => setShowAddModal(true)}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Admin
        </button>
      </div>

      {/* Super Admins Section */}
      <div className="bg-white border border-aqua rounded-xl mb-6 overflow-hidden">
        <div className="bg-lightaqua px-6 py-3 border-b border-aqua/30">
          <h3 className="font-bold font-sans text-lg text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-aqua" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Super Admins
            <span className="text-sm font-normal text-gray-500 ml-2">({superAdmins.length})</span>
          </h3>
        </div>
        <div className="divide-y divide-gray-100">
          {superAdmins.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-400">No super admins found</div>
          ) : (
            superAdmins.map((admin: any) => (
              <div key={admin.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-aqua/20 flex items-center justify-center">
                    <span className="text-aqua font-bold text-lg">
                      {(admin.firstName?.[0] || admin.email?.[0] || "A").toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      {admin.firstName ? `${admin.firstName} ${admin.lastName || ""}` : admin.email}
                      {admin.id === currentUserId && (
                        <span className="ml-2 text-xs bg-aqua/20 text-aqua px-2 py-0.5 rounded-full">You</span>
                      )}
                    </p>
                    {admin.firstName && <p className="text-sm text-gray-500">{admin.email}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium">Super Admin</span>
                  <span className="text-xs text-gray-400 px-2 py-1">Full Access</span>
                  {admin.id !== currentUserId && <AdminDeleteUser id={admin.id} />}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sub Admins Section */}
      <div className="bg-white border border-aqua rounded-xl overflow-hidden">
        <div className="bg-lightaqua px-6 py-3 border-b border-aqua/30">
          <h3 className="font-bold font-sans text-lg text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-aqua" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Sub Admins
            <span className="text-sm font-normal text-gray-500 ml-2">({subAdmins.length})</span>
          </h3>
        </div>

        {subAdmins.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            <p className="text-gray-400 text-lg">No sub-admins yet</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add Admin" to invite team members with limited access</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {subAdmins.map((admin: any) => {
                  const activePerms = getActivePermissions(admin.adminDetails);
                  return (
                    <tr key={admin.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                            <span className="text-amber-700 font-bold">
                              {(admin.firstName?.[0] || admin.email?.[0] || "A").toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">
                              {admin.firstName ? `${admin.firstName} ${admin.lastName || ""}` : admin.email}
                            </p>
                            {admin.firstName && <p className="text-xs text-gray-500">{admin.email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {activePerms.length === 0 ? (
                            <span className="text-xs text-gray-400 italic">No permissions set</span>
                          ) : (
                            activePerms.map((p) => (
                              <span key={p.key} className="text-xs bg-aqua/10 text-aqua px-2 py-0.5 rounded font-medium">{p.label}</span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <AdminPopup adminDetails={admin?.adminDetails} id={admin?.id} />
                          <AdminDeleteUser id={admin.id} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== Add Admin Modal ===== */}
      {showAddModal && (
        <>
          <div className="justify-center items-center flex fixed inset-0 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-[560px] max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-lightaqua px-6 py-4 flex justify-between items-center sticky top-0 z-10">
                <h3 className="font-bold font-sans text-lg">Add New Admin</h3>
                <button
                  onClick={resetModal}
                  className="w-8 h-8 rounded-full bg-aquadark flex items-center justify-center hover:opacity-80 transition-opacity"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit(e);
                }}
                className="p-6"
              >
                {/* Admin Type Selection */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Admin Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setAdminType(1)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        adminType === 1 ? "border-aqua bg-aqua/5" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <svg className={`w-5 h-5 ${adminType === 1 ? "text-aqua" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span className={`font-semibold text-sm ${adminType === 1 ? "text-aqua" : "text-gray-700"}`}>Super Admin</span>
                      </div>
                      <p className="text-xs text-gray-500">Full access to all features</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdminType(2)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        adminType === 2 ? "border-aqua bg-aqua/5" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <svg className={`w-5 h-5 ${adminType === 2 ? "text-aqua" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className={`font-semibold text-sm ${adminType === 2 ? "text-aqua" : "text-gray-700"}`}>Sub Admin</span>
                      </div>
                      <p className="text-xs text-gray-500">Limited access, customizable</p>
                    </button>
                  </div>
                </div>

                {/* Email Input */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email address"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    className={`w-full px-4 py-2.5 border rounded-lg outline-none transition-colors text-sm ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-400 focus:border-red-500"
                        : "border-gray-300 focus:border-aqua"
                    }`}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
                  )}
                </div>

                {/* Password Fields */}
                <div className="mb-5 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                    <input
                      type="password"
                      name="password"
                      placeholder="Set password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      className={`w-full px-4 py-2.5 border rounded-lg outline-none transition-colors text-sm ${
                        formik.touched.password && formik.errors.password
                          ? "border-red-400 focus:border-red-500"
                          : "border-gray-300 focus:border-aqua"
                      }`}
                    />
                    {formik.touched.password && formik.errors.password && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.password}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.confirmPassword}
                      className={`w-full px-4 py-2.5 border rounded-lg outline-none transition-colors text-sm ${
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? "border-red-400 focus:border-red-500"
                          : "border-gray-300 focus:border-aqua"
                      }`}
                    />
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{formik.errors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                {/* Permissions Section - only for Sub Admin */}
                {adminType === 2 && (
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Tab Permissions
                        <span className="text-gray-400 font-normal ml-1">({selectedCount}/10 selected)</span>
                      </label>
                      <button
                        type="button"
                        onClick={selectAllPermissions}
                        className="text-xs text-aqua hover:text-aquadark font-medium transition-colors"
                      >
                        {Object.values(permissions).every(Boolean) ? "Deselect All" : "Select All"}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {permissionLabels.map((perm) => (
                        <label
                          key={perm.key}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                            (permissions as any)[perm.key]
                              ? "border-aqua bg-aqua/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="relative flex items-center">
                            <input
                              type="checkbox"
                              checked={(permissions as any)[perm.key]}
                              onChange={() => togglePermission(perm.key)}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                                (permissions as any)[perm.key] ? "bg-aqua" : "bg-gray-200"
                              }`}
                            >
                              {(permissions as any)[perm.key] && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              (permissions as any)[perm.key] ? "text-gray-800" : "text-gray-600"
                            }`}
                          >
                            {perm.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {selectedCount === 0 && (
                      <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Select at least one permission
                      </p>
                    )}
                  </div>
                )}

                {adminType === 1 && (
                  <div className="mb-5 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs text-emerald-700">
                      <span className="font-semibold">Super Admin</span> will automatically get full access to all tabs.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={resetModal}
                    className="flex-1 py-2.5 border border-aqua text-aqua rounded-lg font-medium hover:bg-aqua/5 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-2.5 bg-aqua text-white rounded-lg font-medium hover:bg-aquadark transition-colors disabled:opacity-50 text-sm"
                  >
                    {isSubmitting ? "Adding..." : `Add ${adminType === 1 ? "Super" : "Sub"} Admin`}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </div>
  );
};
export default FormComponent;
